import { z } from 'zod';
import { baseComponentSchema } from '../component-base.schemas';
import { navbarLogoSchema } from './navbar.schemas';

export const navigationMenuChildLinkSchema = z.object({
  id: z.string().optional(),
  text: z.string().min(1, 'Link text is required'),
  url: z.string().min(1, 'Link URL is required'),
  order: z.number().int().min(0),
  target: z.enum(['_self', '_blank']).optional(),
});

export const navigationMenuLinkSchema = z.object({
  id: z.string().optional(),
  text: z.string().min(1, 'Link text is required'),
  url: z.string().min(1, 'Link URL is required'),
  order: z.number().int().min(0),
  style: z.enum(['normal', 'button', 'outline']).default('normal'),
  target: z.enum(['_self', '_blank']).optional(),
  children: z.array(navigationMenuChildLinkSchema).max(10).optional(),
});

export const navigationMenuCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    brandText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    linkText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    linkHover: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    dropdownBackground: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    dropdownText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    dropdownHover: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const navigationMenuDataSchema = z
  .object({
    logo: navbarLogoSchema.optional(),
    siteName: z.string().min(1, 'Site name is required').optional(),
    links: z.array(navigationMenuLinkSchema).max(12),
    alignment: z.enum(['left', 'center', 'right', 'space-between']).default('space-between'),
    sticky: z.boolean().default(true),
    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    height: z.enum(['compact', 'normal', 'tall']).default('normal').optional(),
    shadow: z.boolean().default(true).optional(),
    borderBottom: z.boolean().default(true).optional(),
    customColors: navigationMenuCustomColorsSchema,
  })
  .refine(data => data.siteName || data.logo, {
    message: 'Either site name or logo must be provided',
  });

export const navigationMenuComponentSchema = baseComponentSchema.extend({
  type: z.literal('navigation-menu'),
  data: navigationMenuDataSchema,
});

export type NavigationMenuChildLink = z.infer<typeof navigationMenuChildLinkSchema>;
export type NavigationMenuLink = z.infer<typeof navigationMenuLinkSchema>;
export type NavigationMenuData = z.infer<typeof navigationMenuDataSchema>;
export type NavigationMenuComponent = z.infer<typeof navigationMenuComponentSchema>;
